import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  GlobalStyles,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Autocomplete,
  useTheme,
} from '@mui/material';
import {
  CampaignRounded,
  PeopleRounded,
  SchoolRounded,
  ClassRounded,
  CalendarMonthRounded,
  AccountBalanceRounded,
  AccessTimeRounded,
  AllInclusiveRounded,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import type React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { TenantDateTimeField } from '../../../components/TenantDateTimeField';
import { errorText } from '../../../lib/error-text';
import {
  announcementApi,
  type CreateAnnouncementDto,
  type UpdateAnnouncementDto,
  type AudienceScope,
  type AudienceTarget,
} from '../../../lib/api/announcement.api';

// ─── Types for selector data ──────────────────────────────────────────────────

interface SelectOption {
  id: string;
  label: string;
  group?: string;
}

// Workaround for MUI's exactOptionalPropertyTypes incompatibility in Autocomplete renderInput.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const autocompleteField = (params: any) => params as React.ComponentProps<typeof TextField>;

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AnnouncementInitialData {
  id: string;
  title: string;
  content: string;
  status: 'draft' | 'published';
  audience: AudienceTarget;
  visibility: { visibleFrom: string | null; visibleUntil: string | null };
}

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** When provided, the dialog runs in edit mode instead of create mode */
  initialData?: AnnouncementInitialData;
}

// ─── Quill toolbar ────────────────────────────────────────────────────────────

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

// ─── Default form values ──────────────────────────────────────────────────────

interface FormState {
  title: string;
  content: string;
  status: 'draft' | 'published';
  audienceScope: AudienceScope;
  selectedPrograms: SelectOption[];
  selectedTerms: SelectOption[];
  selectedSections: SelectOption[];
  selectedStudents: SelectOption[];
  visibilityImmediate: boolean;
  visibilityIndefinite: boolean;
  visibleFrom: string;
  visibleUntil: string;
}

const DEFAULT_FORM: FormState = {
  title: '',
  content: '',
  status: 'published',
  audienceScope: 'seminary',
  selectedPrograms: [],
  selectedTerms: [],
  selectedSections: [],
  selectedStudents: [],
  visibilityImmediate: true,
  visibilityIndefinite: true,
  visibleFrom: '',
  visibleUntil: '',
};

// ─── Helpers: rebuild FormState from stored announcement data ─────────────────

/**
 * When loading a saved announcement for editing, we only have the stored
 * audience.ids (Firebase IDs) and audience.label (the human-readable summary
 * kept in Firestore).
 *
 * We reconstruct each SelectOption with `id = id` and `label = id` as a
 * placeholder; the catalog useEffect will later resolve the real label from
 * the live data once the dialog opens and the Autocomplete refreshes.
 *
 * The critical fix: we store the ORIGINAL label on the FIRST option so that
 * if the user saves without touching the audience selector, buildAudience()
 * still produces a human-readable label instead of a raw Firebase ID.
 */
function audienceToFormState(
  audience: AudienceTarget,
): Pick<
  FormState,
  'audienceScope' | 'selectedPrograms' | 'selectedTerms' | 'selectedSections' | 'selectedStudents'
> {
  const empty: { id: string; label: string }[] = [];
  // Split the stored label back into per-ID labels if possible,
  // otherwise fall back to the raw ID as a placeholder (will be replaced
  // once the catalog finishes loading).
  const storedLabels = (audience.label ?? '').split(', ').map((l) => l.trim());
  const ids = (audience.ids ?? []).map((id, i) => ({
    id,
    label: storedLabels[i] && storedLabels[i] !== id ? storedLabels[i] : id,
  }));
  return {
    audienceScope: audience.scope,
    selectedPrograms: audience.scope === 'program' ? ids : empty,
    selectedTerms: audience.scope === 'term' ? ids : empty,
    selectedSections: audience.scope === 'section' ? ids : empty,
    selectedStudents: audience.scope === 'students' ? ids : empty,
  };
}

function buildInitialForm(data?: AnnouncementInitialData): FormState {
  if (!data) return DEFAULT_FORM;
  return {
    title: data.title,
    content: data.content,
    status: data.status,
    visibilityImmediate: data.visibility?.visibleFrom == null,
    visibilityIndefinite: data.visibility?.visibleUntil == null,
    visibleFrom: data.visibility?.visibleFrom ?? '',
    visibleUntil: data.visibility?.visibleUntil ?? '',
    ...audienceToFormState(data.audience),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CreateAnnouncementDialog({ open, onClose, tenantId, initialData }: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!initialData;
  const [form, setForm] = useState<FormState>(() => buildInitialForm(initialData));

  // ── Catalog state ──────────────────────────────────────────────────────
  const [programs, setPrograms] = useState<SelectOption[]>([]);
  const [terms, setTerms] = useState<SelectOption[]>([]);
  const [sections, setSections] = useState<SelectOption[]>([]);
  const [students, setStudents] = useState<SelectOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Re-sync form when initialData changes (e.g. user clicks Edit on a different announcement)
  useEffect(() => {
    if (open) setForm(buildInitialForm(initialData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  // Load catalog data once the dialog opens
  useEffect(() => {
    if (!open || !tenantId) return;
    setCatalogLoading(true);

    const load = async () => {
      const [progSnap, termSnap, secSnap, courseSnap, peopleSnap] = await Promise.all([
        getDocs(query(collection(db, `tenants/${tenantId}/programs`), orderBy('name'))),
        getDocs(query(collection(db, `tenants/${tenantId}/terms`), orderBy('startDate', 'desc'))),
        getDocs(collection(db, `tenants/${tenantId}/sections`)),
        getDocs(collection(db, `tenants/${tenantId}/courses`)),
        getDocs(
          query(collection(db, `tenants/${tenantId}/people`), where('deletedAt', '==', null)),
        ),
      ]);

      // Build lookup maps for cross-collection resolution.
      // Only include ACTIVE, non-deleted courses so sections referencing
      // a deleted course are implicitly excluded from the dropdown.
      const termById = new Map(termSnap.docs.map((d) => [d.id, d.data()]));
      const courseById = new Map(
        courseSnap.docs
          .filter((d) => !d.data().deletedAt && d.data().status === 'active')
          .map((d) => [d.id, d.data()]),
      );
      const programById = new Map(
        progSnap.docs
          .filter((d) => !d.data().deletedAt && d.data().status !== 'inactive')
          .map((d) => [d.id, d.data().name as string]),
      );

      // Programs: exclude soft-deleted AND inactive records
      const builtPrograms = progSnap.docs
        .filter((d) => !d.data().deletedAt && d.data().status !== 'inactive')
        .map((d) => ({ id: d.id, label: (d.data().name as string) || d.id }));
      setPrograms(builtPrograms);

      // Terms: exclude soft-deleted AND cancelled records; show name + year
      const builtTerms = termSnap.docs
        .filter((d) => !d.data().deletedAt && d.data().status !== 'cancelled')
        .map((d) => {
          const data = d.data();
          const name = (data.name as string) || d.id;
          const year = data.startDate
            ? new Date(
                data.startDate.seconds ? data.startDate.seconds * 1000 : data.startDate,
              ).getFullYear()
            : null;
          return { id: d.id, label: year ? `${name} (${year})` : name };
        });
      setTerms(builtTerms);

      // Sections: exclude soft-deleted AND cancelled records.
      // Also exclude sections whose course no longer exists in courseById
      // (i.e. the course was deleted or deactivated).
      // Resolve names via lookup maps and group by program for smart ordering.
      const builtSections: SelectOption[] = secSnap.docs
        .filter((d) => {
          const data = d.data();
          if (data.deletedAt || data.status === 'cancelled') return false;
          // Drop orphaned sections (course deleted/inactive)
          if (data.courseId && !courseById.has(data.courseId as string)) return false;
          return true;
        })
        .map((d) => {
          const data = d.data();
          const code = data.sectionCode as string | undefined;
          const courseData = data.courseId ? courseById.get(data.courseId as string) : undefined;
          const termData = data.termId ? termById.get(data.termId as string) : undefined;
          const courseName = courseData?.name as string | undefined;
          const termName = termData?.name as string | undefined;
          const programName = data.programId
            ? (programById.get(data.programId as string) ?? 'Sin programa')
            : 'Sin programa';
          // Pattern: "CÓDIGO — Nombre del Curso (Período)"
          const parts: string[] = [];
          if (code) parts.push(code);
          if (courseName) parts.push(courseName);
          const identifier = parts.join(' — ') || d.id;
          const label = termName ? `${identifier} (${termName})` : identifier;
          return { id: d.id, label, group: programName };
        })
        // Sort: first by program name, then alphabetically by label within group
        .sort(
          (a, b) =>
            (a.group ?? '').localeCompare(b.group ?? '', 'es') ||
            a.label.localeCompare(b.label, 'es'),
        );
      setSections(builtSections);

      const builtStudents = peopleSnap.docs
        .filter((d) => d.data().types?.includes('student'))
        .map((d) => {
          const data = d.data();
          const name =
            [data.firstName, data.lastName].filter(Boolean).join(' ') || data.displayName || d.id;
          return { id: d.id, label: name };
        });
      setStudents(builtStudents);

      // The critical fix continued: override any placeholder IDs in the form
      // state with the fully resolved, human-readable labels from the live data.
      setForm((prev) => {
        const next = { ...prev };
        if (prev.audienceScope === 'program' && prev.selectedPrograms.length > 0) {
          next.selectedPrograms = prev.selectedPrograms.map(
            (p) => builtPrograms.find((o) => o.id === p.id) || p,
          );
        } else if (prev.audienceScope === 'term' && prev.selectedTerms.length > 0) {
          next.selectedTerms = prev.selectedTerms.map(
            (t) => builtTerms.find((o) => o.id === t.id) || t,
          );
        } else if (prev.audienceScope === 'section' && prev.selectedSections.length > 0) {
          next.selectedSections = prev.selectedSections.map(
            (s) => builtSections.find((o) => o.id === s.id) || s,
          );
        } else if (prev.audienceScope === 'students' && prev.selectedStudents.length > 0) {
          next.selectedStudents = prev.selectedStudents.map(
            (s) => builtStudents.find((o) => o.id === s.id) || s,
          );
        }
        return next;
      });
      setCatalogLoading(false);
    };

    load().catch(() => setCatalogLoading(false));
  }, [open, tenantId]);

  // ── Mutation ────────────────────────────────────────────────────────────
  const { mutate, isPending, error, reset } = useMutation<void, Error>({
    mutationFn: async () => {
      const audience = buildAudience();
      const visibility = buildVisibility();
      if (isEditMode && initialData) {
        const dto: UpdateAnnouncementDto = {
          tenantId,
          announcementId: initialData.id,
          title: form.title.trim(),
          content: form.content,
          status: form.status,
          audience,
          visibility,
        };
        await announcementApi.updateAnnouncement(dto);
        return;
      }
      const dto: CreateAnnouncementDto = {
        tenantId,
        title: form.title.trim(),
        content: form.content,
        status: form.status,
        audience,
        visibility,
      };
      await announcementApi.createAnnouncement(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements', tenantId] });
      handleClose();
    },
  });

  // ── Helpers ─────────────────────────────────────────────────────────────
  const buildAudience = (): AudienceTarget => {
    switch (form.audienceScope) {
      case 'seminary':
        return { scope: 'seminary', ids: [], label: 'Todo el seminario' };
      case 'program':
        return {
          scope: 'program',
          ids: form.selectedPrograms.map((p) => p.id),
          label: form.selectedPrograms.map((p) => p.label).join(', '),
        };
      case 'term':
        return {
          scope: 'term',
          ids: form.selectedTerms.map((t) => t.id),
          label: form.selectedTerms.map((t) => t.label).join(', '),
        };
      case 'section':
        return {
          scope: 'section',
          ids: form.selectedSections.map((s) => s.id),
          label: form.selectedSections.map((s) => s.label).join(', '),
        };
      case 'students':
        return {
          scope: 'students',
          ids: form.selectedStudents.map((s) => s.id),
          label: form.selectedStudents.map((s) => s.label).join(', '),
        };
    }
  };

  const buildVisibility = () => ({
    visibleFrom: form.visibilityImmediate ? null : form.visibleFrom || null,
    visibleUntil: form.visibilityIndefinite ? null : form.visibleUntil || null,
  });

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    reset();
    onClose();
  };

  const field = <K extends keyof FormState>(key: K, val: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const isContentEmpty =
    !form.content || form.content === '<p><br></p>' || form.content.trim() === '';
  const hasAudienceTarget =
    form.audienceScope === 'seminary' ||
    (form.audienceScope === 'program' && form.selectedPrograms.length > 0) ||
    (form.audienceScope === 'term' && form.selectedTerms.length > 0) ||
    (form.audienceScope === 'section' && form.selectedSections.length > 0) ||
    (form.audienceScope === 'students' && form.selectedStudents.length > 0);

  const isValid = form.title.trim().length > 0 && !isContentEmpty && hasAudienceTarget;

  // ── Audience scope icons ─────────────────────────────────────────────────
  const SCOPE_OPTIONS: {
    value: AudienceScope;
    label: string;
    icon: React.ReactNode;
    description: string;
  }[] = [
    {
      value: 'seminary',
      icon: <AccountBalanceRounded fontSize="small" />,
      label: 'Todo el seminario',
      description: 'Todos los alumnos del seminario',
    },
    {
      value: 'program',
      icon: <SchoolRounded fontSize="small" />,
      label: 'Programa de estudio',
      description: 'Alumnos de un programa específico',
    },
    {
      value: 'term',
      icon: <CalendarMonthRounded fontSize="small" />,
      label: 'Ciclo / Período',
      description: 'Alumnos de un ciclo académico',
    },
    {
      value: 'section',
      icon: <ClassRounded fontSize="small" />,
      label: 'Clase / Sección',
      description: 'Alumnos de una clase en particular',
    },
    {
      value: 'students',
      icon: <PeopleRounded fontSize="small" />,
      label: 'Alumnos específicos',
      description: 'Seleccionar alumnos individualmente',
    },
  ];

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Real colour values for GlobalStyles (sx tokens don't resolve for SVG stroke/fill)
  const iconColor = isDark ? 'rgba(255,255,255,0.70)' : 'rgba(0,0,0,0.60)';
  const iconHover = '#10b981';
  const toolbarBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const toolbarBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.23)';
  const editorBg = isDark ? theme.palette.background.default : '#fff';
  const editorColor = isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)';
  const placeholderClr = isDark ? 'rgba(255,255,255,0.38)' : 'rgba(0,0,0,0.38)';
  const dropdownBg = theme.palette.background.paper;
  const dropdownColor = editorColor;

  return (
    <>
      <GlobalStyles
        styles={{
          '.announcement-quill-editor .ql-toolbar': {
            background: `${toolbarBg} !important`,
            borderColor: `${toolbarBorder} !important`,
          },
          '.announcement-quill-editor .ql-toolbar .ql-stroke': {
            stroke: `${iconColor} !important`,
            fill: 'none !important',
          },
          '.announcement-quill-editor .ql-toolbar .ql-fill': {
            fill: `${iconColor} !important`,
            stroke: 'none !important',
          },
          '.announcement-quill-editor .ql-toolbar .ql-picker-label': {
            color: `${iconColor} !important`,
          },
          '.announcement-quill-editor .ql-toolbar .ql-picker-label:hover .ql-stroke': {
            stroke: `${iconHover} !important`,
          },
          '.announcement-quill-editor .ql-toolbar button:hover .ql-stroke': {
            stroke: `${iconHover} !important`,
          },
          '.announcement-quill-editor .ql-toolbar button:hover .ql-fill': {
            fill: `${iconHover} !important`,
          },
          '.announcement-quill-editor .ql-toolbar .ql-active .ql-stroke': {
            stroke: `${iconHover} !important`,
          },
          '.announcement-quill-editor .ql-toolbar .ql-active .ql-fill': {
            fill: `${iconHover} !important`,
          },
          '.announcement-quill-editor .ql-toolbar .ql-picker-options': {
            background: `${dropdownBg} !important`,
            color: `${dropdownColor} !important`,
            borderColor: `${toolbarBorder} !important`,
          },
          '.announcement-quill-editor .ql-container': {
            background: `${editorBg} !important`,
            borderColor: `${toolbarBorder} !important`,
          },
          '.announcement-quill-editor .ql-editor': {
            color: `${editorColor} !important`,
          },
          '.announcement-quill-editor .ql-editor.ql-blank::before': {
            color: `${placeholderClr} !important`,
            fontStyle: 'normal !important',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}
      >
        {/* ── Title ─────────────────────────────────────────────────────── */}
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CampaignRounded sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {isEditMode ? 'Editar Anuncio' : 'Crear Nuevo Anuncio'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Redacta y dirige el comunicado a tu audiencia
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Stack spacing={3}>
            {/* Error */}
            {error && (
              <Alert severity="error">{errorText(error, 'Error al crear el anuncio')}</Alert>
            )}

            {/* ── Título ─────────────────────────────────────────────── */}
            <TextField
              label="Título del anuncio"
              value={form.title}
              onChange={(e) => field('title', e.target.value)}
              fullWidth
              required
              autoFocus
              placeholder="Ej: Bienvenida al nuevo semestre"
              size="medium"
            />

            {/* ── Editor de texto enriquecido ───────────────────────── */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: 600 }}
              >
                Contenido del anuncio *
              </Typography>
              <Box
                className="announcement-quill-editor"
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  '& .ql-container': {
                    minHeight: 200,
                    fontFamily: 'inherit',
                    fontSize: '0.9375rem',
                  },
                  '& .ql-editor': { minHeight: 200 },
                }}
              >
                <ReactQuill
                  theme="snow"
                  modules={QUILL_MODULES}
                  value={form.content}
                  onChange={(val) => field('content', val)}
                  placeholder="Escribe el contenido del anuncio aquí..."
                />
              </Box>
            </Box>

            {/* ── Audiencia ──────────────────────────────────────────── */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
              >
                AUDIENCIA OBJETIVO
              </Typography>
              <Stack spacing={1.5}>
                {/* Scope selector cards */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
                    gap: 1,
                  }}
                >
                  {SCOPE_OPTIONS.map((opt) => (
                    <Box
                      key={opt.value}
                      onClick={() => field('audienceScope', opt.value)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: form.audienceScope === opt.value ? '#10b981' : 'divider',
                        bgcolor:
                          form.audienceScope === opt.value
                            ? 'rgba(16,185,129,0.08)'
                            : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        textAlign: 'center',
                        '&:hover': {
                          borderColor: '#10b981',
                          bgcolor: 'rgba(16,185,129,0.05)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: form.audienceScope === opt.value ? '#10b981' : 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {opt.icon}
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={form.audienceScope === opt.value ? '#10b981' : 'text.primary'}
                        sx={{ display: 'block', lineHeight: 1.2 }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Contextual selectors */}
                {form.audienceScope === 'program' && (
                  <Autocomplete
                    multiple
                    options={programs}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={form.selectedPrograms}
                    onChange={(_, val) => field('selectedPrograms', val)}
                    loading={catalogLoading}
                    renderTags={(val, getTagProps) =>
                      val.map((opt, i) => (
                        <Chip
                          {...getTagProps({ index: i })}
                          key={opt.id}
                          label={opt.label}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...autocompleteField(params)}
                        variant="outlined"
                        label="Programas de estudio"
                        placeholder="Buscar programa..."
                        size="small"
                      />
                    )}
                  />
                )}
                {form.audienceScope === 'term' && (
                  <Autocomplete
                    multiple
                    options={terms}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={form.selectedTerms}
                    onChange={(_, val) => field('selectedTerms', val)}
                    loading={catalogLoading}
                    renderTags={(val, getTagProps) =>
                      val.map((opt, i) => (
                        <Chip
                          {...getTagProps({ index: i })}
                          key={opt.id}
                          label={opt.label}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...autocompleteField(params)}
                        variant="outlined"
                        label="Ciclos / Períodos académicos"
                        placeholder="Buscar ciclo..."
                        size="small"
                      />
                    )}
                  />
                )}
                {form.audienceScope === 'section' && (
                  <Autocomplete
                    multiple
                    options={sections}
                    getOptionLabel={(o) => o.label}
                    groupBy={(o) => o.group ?? 'Sin programa'}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={form.selectedSections}
                    onChange={(_, val) => field('selectedSections', val)}
                    loading={catalogLoading}
                    renderTags={(val, getTagProps) =>
                      val.map((opt, i) => (
                        <Chip
                          {...getTagProps({ index: i })}
                          key={opt.id}
                          label={opt.label}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...autocompleteField(params)}
                        variant="outlined"
                        label="Clases / Secciones"
                        placeholder="Buscar clase..."
                        size="small"
                      />
                    )}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <Box
                          sx={{
                            px: 2,
                            pt: 1.5,
                            pb: 0.5,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: 'text.disabled',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {params.group}
                        </Box>
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                  />
                )}
                {form.audienceScope === 'students' && (
                  <Autocomplete
                    multiple
                    options={students}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    value={form.selectedStudents}
                    onChange={(_, val) => field('selectedStudents', val)}
                    loading={catalogLoading}
                    renderTags={(val, getTagProps) =>
                      val.map((opt, i) => (
                        <Chip
                          {...getTagProps({ index: i })}
                          key={opt.id}
                          label={opt.label}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...autocompleteField(params)}
                        variant="outlined"
                        label="Alumnos"
                        placeholder="Buscar alumno..."
                        size="small"
                      />
                    )}
                  />
                )}
              </Stack>
            </Box>

            {/* ── Visibilidad ────────────────────────────────────────── */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}
              >
                VISIBILIDAD
              </Typography>
              <Stack spacing={2}>
                {/* Desde */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <FormControlLabel
                    sx={{ minWidth: 200, m: 0 }}
                    control={
                      <Switch
                        checked={form.visibilityImmediate}
                        onChange={(e) => field('visibilityImmediate', e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccessTimeRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">Visibilidad inmediata</Typography>
                      </Stack>
                    }
                  />
                  {!form.visibilityImmediate && (
                    <TenantDateTimeField
                      label="Visible desde"
                      value={form.visibleFrom}
                      onChange={(v) => field('visibleFrom', v)}
                      fullWidth
                    />
                  )}
                </Stack>

                {/* Hasta */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <FormControlLabel
                    sx={{ minWidth: 200, m: 0 }}
                    control={
                      <Switch
                        checked={form.visibilityIndefinite}
                        onChange={(e) => field('visibilityIndefinite', e.target.checked)}
                        color="success"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <AllInclusiveRounded sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">Sin fecha de vencimiento</Typography>
                      </Stack>
                    }
                  />
                  {!form.visibilityIndefinite && (
                    <TenantDateTimeField
                      label="Visible hasta"
                      value={form.visibleUntil}
                      onChange={(v) => field('visibleUntil', v)}
                      fullWidth
                    />
                  )}
                </Stack>
              </Stack>
            </Box>

            {/* ── Estado inicial ─────────────────────────────────────── */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
                Estado inicial:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={form.status}
                  onChange={(e) => field('status', e.target.value as 'draft' | 'published')}
                >
                  <MenuItem value="published">✅ Publicar ahora</MenuItem>
                  <MenuItem value="draft">📝 Guardar como borrador</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={isPending} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => mutate()}
            disabled={isPending || !isValid}
            sx={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', px: 3 }}
          >
            {isPending ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>Guardando...</span>
              </Stack>
            ) : isEditMode ? (
              form.status === 'published' ? (
                'Guardar Cambios'
              ) : (
                'Guardar Borrador'
              )
            ) : form.status === 'published' ? (
              'Publicar Anuncio'
            ) : (
              'Guardar Borrador'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
